import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Recycle, Archive, AlertTriangle,
  Filter, Search, Ruler, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const StatusBadge = ({ available }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
    available
      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
      : 'bg-slate-500/15 text-slate-400 border border-slate-500/30'
  }`} data-testid={`remnant-status-${available ? 'available' : 'consumed'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${available ? 'bg-emerald-400' : 'bg-slate-500'}`} />
    {available ? 'Available' : 'Consumed'}
  </span>
);

const StatCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="bg-slate-900/60 border border-slate-700/40 rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-md ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-2xl font-bold font-mono text-slate-100">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
        {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
      </div>
    </div>
  </div>
);

const FormBreakdownBar = ({ data }) => {
  if (!data || data.length === 0) return null;
  const colors = {
    Pipe: 'bg-cyan-500', Round_Bar: 'bg-amber-500', Flat_Bar: 'bg-violet-500',
    Angle: 'bg-rose-500', Channel: 'bg-teal-500', Wire: 'bg-blue-500',
    Beam: 'bg-orange-500',
  };
  const total = data.reduce((s, d) => s + d.total, 0);
  if (total === 0) return null;

  return (
    <div className="bg-slate-900/60 border border-slate-700/40 rounded-lg p-4" data-testid="remnant-form-breakdown">
      <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">By Material Form</div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-800 mb-3">
        {data.map((d) => (
          <div key={d.material_form}
            className={`${colors[d.material_form] || 'bg-slate-500'} transition-all`}
            style={{ width: `${(d.total / total) * 100}%` }}
            title={`${d.material_form}: ${d.total} remnants`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {data.map((d) => (
          <div key={d.material_form} className="flex items-center gap-2 text-[11px]">
            <span className={`w-2.5 h-2.5 rounded-sm ${colors[d.material_form] || 'bg-slate-500'}`} />
            <span className="text-slate-300 font-medium">{d.material_form.replace('_', ' ')}</span>
            <span className="text-emerald-400 font-mono">{d.available}</span>
            <span className="text-slate-600">/</span>
            <span className="text-slate-500 font-mono">{d.total}</span>
            <span className="text-slate-600 text-[10px]">({(d.available_length_mm / 1000).toFixed(1)}m avail)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function RemnantInventoryPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formFilter, setFormFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (formFilter !== 'all') params.set('material_form', formFilter);
      const qs = params.toString();
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/stock-remnants/inventory${qs ? '?' + qs : ''}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      console.error('Failed to load remnant inventory:', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, formFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRemnants = (data?.remnants || []).filter((r) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (r.profile_code || '').toLowerCase().includes(q) ||
      (r.profile_name || '').toLowerCase().includes(q) ||
      (r.material_grade || '').toLowerCase().includes(q) ||
      (r.source_bom_number || '').toLowerCase().includes(q) ||
      (r.source_product_name || '').toLowerCase().includes(q)
    );
  });

  const summary = data?.summary || {};
  const byForm = data?.by_form || [];
  const uniqueForms = [...new Set(byForm.map(f => f.material_form))];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="remnant-inventory-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/bom')} data-testid="remnant-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <Recycle className="w-5 h-5 text-amber-400" />
                Remnant Inventory
              </h1>
              <p className="text-xs text-slate-500">Track usable material remnants across jobs</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/bom')}
            className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="remnant-bom-builder-btn">
            <Package className="w-4 h-4 mr-1.5" /> BOM Builder
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {loading && !data ? (
          <div className="text-center py-20 text-slate-500" data-testid="remnant-loading">Loading remnant inventory...</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Archive} label="Total Remnants" value={summary.total || 0}
                sub={`${((summary.total_length_mm || 0) / 1000).toFixed(1)}m total stock`}
                color="bg-slate-700/50 text-slate-300" testId="remnant-stat-total" />
              <StatCard icon={Recycle} label="Available" value={summary.available || 0}
                sub={`${((summary.available_length_mm || 0) / 1000).toFixed(1)}m ready to use`}
                color="bg-emerald-500/15 text-emerald-400" testId="remnant-stat-available" />
              <StatCard icon={Package} label="Consumed" value={summary.consumed || 0}
                sub="Reused in other jobs"
                color="bg-blue-500/15 text-blue-400" testId="remnant-stat-consumed" />
              <StatCard icon={Ruler} label="Material Forms" value={uniqueForms.length}
                sub={uniqueForms.join(', ').replace(/_/g, ' ') || 'None'}
                color="bg-amber-500/15 text-amber-400" testId="remnant-stat-forms" />
            </div>

            {/* Form Breakdown */}
            <FormBreakdownBar data={byForm} />

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Filter className="w-3.5 h-3.5" /> Filters:
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-900 border-slate-700" data-testid="remnant-filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="consumed">Consumed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formFilter} onValueChange={setFormFilter}>
                <SelectTrigger className="w-[160px] h-8 text-xs bg-slate-900 border-slate-700" data-testid="remnant-filter-form">
                  <SelectValue placeholder="Material Form" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Forms</SelectItem>
                  {uniqueForms.map(f => (
                    <SelectItem key={f} value={f}>{f.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input
                  placeholder="Search profile, grade, BOM..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs bg-slate-900 border-slate-700"
                  data-testid="remnant-search"
                />
              </div>
              <div className="ml-auto text-[11px] text-slate-500">
                {filteredRemnants.length} remnant{filteredRemnants.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Table */}
            <div className="border border-slate-700/40 rounded-lg overflow-hidden" data-testid="remnant-table">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-900/80 border-b border-slate-700/40">
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Status</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Profile</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Grade</th>
                    <th className="text-right px-3 py-2.5 text-slate-500 font-semibold">Length</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Source BOM</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Bar #</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Consumed By</th>
                    <th className="text-left px-3 py-2.5 text-slate-500 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRemnants.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-slate-600">
                        <Archive className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        No remnants found
                      </td>
                    </tr>
                  ) : filteredRemnants.map((r) => (
                    <tr key={r.remnant_id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      data-testid={`remnant-row-${r.remnant_id}`}>
                      <td className="px-3 py-2.5">
                        <StatusBadge available={r.is_available} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-slate-200 font-mono font-medium">{r.profile_code}</div>
                        <div className="text-[10px] text-slate-600 truncate max-w-[180px]">{r.profile_name}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-slate-300">{r.material_grade}</span>
                        <div className="text-[10px] text-slate-600">{(r.material_form || '').replace('_', ' ')}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="font-mono text-amber-300 font-semibold">{r.length_mm}</span>
                        <span className="text-slate-600 ml-0.5">mm</span>
                        <div className="text-[10px] text-slate-600">{(r.length_mm / 1000).toFixed(2)}m</div>
                      </td>
                      <td className="px-3 py-2.5">
                        {r.source_bom_number ? (
                          <button onClick={() => navigate(`/bom/${r.source_bom_id}`)}
                            className="text-cyan-400 hover:text-cyan-300 hover:underline font-mono">
                            {r.source_bom_number}
                          </button>
                        ) : <span className="text-slate-600">—</span>}
                        <div className="text-[10px] text-slate-600 truncate max-w-[140px]">{r.source_product_name}</div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-400 font-mono">{r.source_bar_index || '—'}</td>
                      <td className="px-3 py-2.5">
                        {r.consumed_by_bom ? (
                          <>
                            <button onClick={() => navigate(`/bom/${r.consumed_by_bom}`)}
                              className="text-blue-400 hover:text-blue-300 hover:underline font-mono">
                              {r.consumed_by_bom_number || r.consumed_by_bom.slice(0, 8)}
                            </button>
                            <div className="text-[10px] text-slate-600 truncate max-w-[140px]">{r.consumed_by_product_name}</div>
                          </>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">
                        <div>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</div>
                        {r.consumed_at && (
                          <div className="text-[10px] text-slate-600 flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" />
                            {new Date(r.consumed_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* No remnants guidance */}
            {summary.total === 0 && (
              <div className="text-center py-8 border border-dashed border-slate-700/40 rounded-lg" data-testid="remnant-empty-state">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-amber-500/40" />
                <div className="text-sm text-slate-400 mb-1">No remnants logged yet</div>
                <div className="text-xs text-slate-600 max-w-md mx-auto">
                  Remnants are logged from the Nesting Summary panel in the BOM Editor.
                  Open a BOM with linear stock, view the nesting plan, and click "Log Remnants" to start tracking.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
